import { useRef } from "react";
import cx from "ui/cx";
import type { SelectProps, SelectRef } from "ui/forms/Select";
import Select from "ui/forms/Select";
import styles from "./ExpandableSelect.module.scss";

export interface ExpandableSelectProps extends SelectProps {
  optionsAlignment?: "left" | "right";
  getLeftOptionsBoundary?: () => number | undefined;
  getRightOptionsBoundary?: () => number | undefined;
}

export const OPTIONS_BOUNDARY_PADDING = 12;
const MAX_OPTIONS_WIDTH = 336;

export const RIGHT_ALIGNED_OPTIONS_CLASS = "rightAligned";

function getWindowRightBoundary() {
  return window.innerWidth;
}

function getWindowLeftBoundary() {
  return 0;
}

export default function ExpandableSelect({
  optionsAlignment = "left",
  getLeftOptionsBoundary = getWindowLeftBoundary,
  getRightOptionsBoundary = getWindowRightBoundary,
  className,
  ...selectProps
}: ExpandableSelectProps) {
  const selectRef = useRef<SelectRef>(null);

  const setLeftAlignedOptionsStyles = (rect: DOMRect) => {
    if (getRightOptionsBoundary) {
      const r = getRightOptionsBoundary();
      if (typeof r === "number") {
        const rightBoundary = Math.min(r, getWindowRightBoundary()) - OPTIONS_BOUNDARY_PADDING;
        // constrain the options container to extend from the
        // left side of the input to the given rightmost boundary
        const w = rightBoundary - rect.left;
        if (selectRef?.current?.options) {
          selectRef.current.options.style.maxWidth = `${Math.min(w, MAX_OPTIONS_WIDTH)}px`;
        }
      }
    }
  };

  const setRightAlignedOptionsStyles = (rect: DOMRect) => {
    if (getLeftOptionsBoundary) {
      const l = getLeftOptionsBoundary();
      if (typeof l === "number") {
        const leftBoundary = Math.max(l, getWindowLeftBoundary()) + OPTIONS_BOUNDARY_PADDING;
        // constrain the options container to extend from the
        // right side of the input to the given leftmost boundary
        const w = rect.right - leftBoundary;
        if (selectRef?.current?.options) {
          selectRef.current.options.style.maxWidth = `${Math.min(w, MAX_OPTIONS_WIDTH)}px`;
        }
      }
    }
  };

  const onOpen = () => {
    if (selectRef?.current?.input && selectRef?.current?.options) {
      const rect = selectRef.current.input.getBoundingClientRect();
      if (optionsAlignment === "left") {
        setLeftAlignedOptionsStyles(rect);
      } else {
        setRightAlignedOptionsStyles(rect);
      }
    }
  };

  return (
    <Select
      {...selectProps}
      className={cx(
        styles.wrapper,
        styles.expandable,
        optionsAlignment === "left" && styles.leftAligned,
        optionsAlignment === "right" && styles[RIGHT_ALIGNED_OPTIONS_CLASS],
        className,
      )}
      showOptionsGap
      onOpen={onOpen}
      ref={selectRef}
    />
  );
}
